import { useEffect } from "react";
import useReveal from "@/hooks/useReveal";
import { Navbar } from "@/components/site/Navbar";
import { Hero } from "@/components/site/Hero";
import { InstitutionalVideo } from "@/components/site/InstitutionalVideo";
import { PieceStory } from "@/components/site/PieceStory";
import { About } from "@/components/site/About";
import { Solutions } from "@/components/site/Solutions";
import { Nationalization } from "@/components/site/Nationalization";
import { Differentials } from "@/components/site/Differentials";
import { Markets } from "@/components/site/Markets";
import { Categories } from "@/components/site/Categories";
import { HowWeWork } from "@/components/site/HowWeWork";
import { Indicators } from "@/components/site/Indicators";
import { FAQSection } from "@/components/site/FAQSection";
import { CTASection } from "@/components/site/CTASection";
import { Contact } from "@/components/site/Contact";
import { Footer } from "@/components/site/Footer";
import { WhatsAppFAB } from "@/components/site/WhatsAppFAB";

export default function Home() {
  useReveal();

  useEffect(() => {
    document.title = "Imporparts — Soluções Corporativas e Industriais";
    const setMeta = (name, content, attr = "name") => {
      let el = document.head.querySelector(`meta[${attr}="${name}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, name);
        document.head.appendChild(el);
      }
      el.setAttribute("content", content);
    };
    setMeta(
      "description",
      "Imporparts — especialistas em soluções corporativas e industriais: engenharia de suprimentos, nacionalização de componentes, importação especializada e continuidade operacional."
    );
    setMeta("theme-color", "#0a192f");
    setMeta("og:title", "Imporparts — Soluções Corporativas e Industriais", "property");
    setMeta(
      "og:description",
      "Mantemos operações em movimento. Engenharia de suprimentos, nacionalização de componentes e importação especializada.",
      "property"
    );
    setMeta("og:type", "website", "property");
    setMeta("og:locale", "pt_BR", "property");
    setMeta("og:site_name", "Imporparts", "property");
    setMeta("twitter:card", "summary_large_image");
    setMeta("twitter:title", "Imporparts — Soluções Corporativas e Industriais");
    setMeta("twitter:description", "Mantemos operações em movimento. Engenharia de suprimentos, nacionalização de componentes e importação especializada.");
    // canonical
    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", "https://imporparts.com.br/");

    // JSON-LD structured data (Organization, LocalBusiness, FAQPage, BreadcrumbList)
    const ldId = "imporparts-jsonld";
    let ld = document.getElementById(ldId);
    if (!ld) {
      ld = document.createElement("script");
      ld.type = "application/ld+json";
      ld.id = ldId;
      document.head.appendChild(ld);
    }
    ld.textContent = JSON.stringify([
      {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: "Imporparts",
        legalName: "Imporparts — Soluções Corporativas e Industriais",
        url: "https://imporparts.com.br/",
        logo: "https://imporparts.com.br/logo.png",
        description:
          "Especialistas em soluções corporativas e industriais: engenharia de suprimentos, nacionalização de componentes, importação especializada e continuidade operacional.",
        telephone: "+55-11-2670-4419",
        email: "comercial@imporparts.com.br",
        address: {
          "@type": "PostalAddress",
          streetAddress: "Rua Tânia, 329",
          addressLocality: "Carapicuíba",
          addressRegion: "SP",
          postalCode: "06327",
          addressCountry: "BR",
        },
        contactPoint: [{
          "@type": "ContactPoint",
          telephone: "+55-11-98896-0809",
          contactType: "sales",
          areaServed: "BR",
          availableLanguage: ["Portuguese"],
        }],
      },
      {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        "@id": "https://imporparts.com.br/#business",
        name: "Imporparts",
        image: "https://imporparts.com.br/logo.png",
        priceRange: "$$$",
        address: {
          "@type": "PostalAddress",
          streetAddress: "Rua Tânia, 329",
          addressLocality: "Carapicuíba",
          addressRegion: "SP",
          addressCountry: "BR",
        },
        openingHoursSpecification: [{
          "@type": "OpeningHoursSpecification",
          dayOfWeek: [
            "Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"
          ],
          opens: "08:00",
          closes: "18:00",
        }],
        telephone: "+55-11-2670-4419",
      },
      {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: [
          {"@type":"Question","name":"O que são Soluções Corporativas e Industriais da Imporparts?","acceptedAnswer":{"@type":"Answer","text":"Somos parceiros estratégicos em engenharia de suprimentos, nacionalização de componentes, importação especializada, gestão de itens críticos e atendimento consultivo para manter a operação industrial em movimento."}},
          {"@type":"Question","name":"Como funciona a nacionalização de componentes?","acceptedAnswer":{"@type":"Answer","text":"Analisamos tecnicamente o componente importado, desenvolvemos ou identificamos um equivalente nacional homologado e entregamos a solução com a mesma performance, reduzindo custo, prazo e dependência do mercado externo."}},
          {"@type":"Question","name":"Vocês trabalham com peças descontinuadas?","acceptedAnswer":{"@type":"Answer","text":"Sim. Localizamos e desenvolvemos alternativas para componentes fora de linha, com laudo técnico e rastreabilidade."}},
          {"@type":"Question","name":"Vocês fornecem empilhadeiras, pontes rolantes, compressores e máquinas de jateamento?","acceptedAnswer":{"@type":"Answer","text":"Sim. Fornecemos equipamentos industriais completos — empilhadeiras, pontes rolantes, compressores de ar (diesel e elétricos) e máquinas de jateamento — além de todas as peças de reposição e manutenção."}}
        ],
      },
      {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          {"@type":"ListItem","position":1,"name":"Início","item":"https://imporparts.com.br/"},
          {"@type":"ListItem","position":2,"name":"Soluções Corporativas e Industriais","item":"https://imporparts.com.br/#solucoes"},
          {"@type":"ListItem","position":3,"name":"Nacionalização de Componentes","item":"https://imporparts.com.br/#nacionalizacao"},
          {"@type":"ListItem","position":4,"name":"Mercados Atendidos","item":"https://imporparts.com.br/#mercados"},
          {"@type":"ListItem","position":5,"name":"Categorias","item":"https://imporparts.com.br/#categorias"},
          {"@type":"ListItem","position":6,"name":"Contato","item":"https://imporparts.com.br/#contato"}
        ],
      },
    ]);
  }, []);

  return (
    <div className="min-h-screen bg-white text-slate-900 font-body antialiased">
      <Navbar />
      <main>
        <Hero />
        <InstitutionalVideo />
        <PieceStory />
        <About />
        <Solutions />
        <Nationalization />
        <Differentials />
        <Markets />
        <Categories />
        <HowWeWork />
        <Indicators />
        <FAQSection />
        <CTASection />
        <Contact />
      </main>
      <Footer />
      <WhatsAppFAB />
    </div>
  );
}
